import type { IProductP85Row, TrendDirection } from '@skystone/shared';
import { DeltaLabel, formatPctText } from '@/components/shared/delta-label';

interface ICycleTimeP85StackedProps {
  rows: IProductP85Row[];
  groupLabel: 'PRODUCT' | 'TEAM';
  /** Team name resolved by the page; overrides the per-row product label when set. */
  selectedTeamName?: string | null;
}

function StackedRow({
  label,
  p85,
  deltaPct,
  direction,
}: Readonly<{
  label: string;
  p85: number | null;
  deltaPct: number | null;
  direction: TrendDirection | null;
}>) {
  if (p85 === null) {
    return (
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-muted-foreground">— no data —</span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="inline-flex items-center gap-1.5 tabular-nums">
        <span className="font-medium text-foreground">{Math.round(p85)}d</span>
        <DeltaLabel direction={direction} text={formatPctText(deltaPct)} higherIsBetter={false} />
      </span>
    </div>
  );
}

/**
 * Narrow-width fallback for the Cycle Time P85 table. Renders each row as a
 * small card with the group name on top and Story/Bug/Task rows stacked
 * vertically below. Used below the `stacked` threshold where the 4-col
 * table can't render legibly.
 */
export function CycleTimeP85Stacked({
  rows,
  groupLabel: _groupLabel,
  selectedTeamName = null,
}: Readonly<ICycleTimeP85StackedProps>) {
  if (rows.length === 0) return null;

  return (
    <ul className="space-y-3">
      {rows.map((row) => (
        <li key={row.productName} className="space-y-1 rounded-md border border-border p-2">
          <div className="text-xs font-semibold text-foreground">
            {selectedTeamName ?? row.productName}
          </div>
          <StackedRow
            label="All"
            p85={row.overallP85}
            deltaPct={row.overallDeltaPct}
            direction={row.overallTrendDirection}
          />
          <StackedRow
            label="Story"
            p85={row.storyP85}
            deltaPct={row.storyDeltaPct}
            direction={row.storyTrendDirection}
          />
          <StackedRow
            label="Bug"
            p85={row.bugP85}
            deltaPct={row.bugDeltaPct}
            direction={row.bugTrendDirection}
          />
          <StackedRow
            label="Task"
            p85={row.taskP85}
            deltaPct={row.taskDeltaPct}
            direction={row.taskTrendDirection}
          />
        </li>
      ))}
    </ul>
  );
}
